import * as Cesium from 'cesium'

// LAS/LAZ 원본에서 변환된 포인트 클라우드(pnts) 여부 추정 — 폴더/ZIP 업로드 내부 LAS는 감지하지 못함
export function isLasSource(originalFileName: string): boolean {
  const lower = originalFileName.toLowerCase()
  return lower.endsWith('.las') || lower.endsWith('.laz')
}

export type PointStyleMode = 'rgb' | 'classification' | 'intensity-gray' | 'intensity-heat' | 'height'

export const POINT_STYLE_MODES: { val: PointStyleMode; label: string }[] = [
  { val: 'rgb', label: '원본 색상 (RGB)' },
  { val: 'classification', label: '분류별 색상 (Classification)' },
  { val: 'intensity-gray', label: '반사강도 흑백 (Intensity)' },
  { val: 'intensity-heat', label: '반사강도 히트맵 (Intensity)' },
  { val: 'height', label: '높이별 색상 (Elevation)' },
]

// 레전드(범례)가 필요한 모드 — 값이 실제 물리량(분류 코드·반사강도·고도)을 색으로 매핑하는 모드만 해당
export const LEGEND_MODES: PointStyleMode[] = ['classification', 'intensity-heat', 'height']

export interface PointStyleState {
  mode: PointStyleMode
  pointSize: number
  attenuation: boolean
}

export const DEFAULT_POINT_STYLE: PointStyleState = {
  mode: 'rgb',
  pointSize: 2,
  attenuation: false,
}

// ASPRS 분류 코드 → 색상. 셰이더 생성과 범례 렌더링이 이 표 하나를 공유한다.
export const CLASSIFICATION_LEGEND: { code: number; label: string; color: string }[] = [
  { code: 0, label: '생성/미분류', color: '#bfbfbf' },
  { code: 1, label: '미분류', color: '#808080' },
  { code: 2, label: '지면', color: '#8b4513' },
  { code: 3, label: '낮은 식생', color: '#9acd32' },
  { code: 4, label: '중간 식생', color: '#008000' },
  { code: 5, label: '높은 식생', color: '#006400' },
  { code: 6, label: '건물', color: '#00ffff' },
  { code: 7, label: '노이즈', color: '#ff0000' },
  { code: 9, label: '수면', color: '#0000ff' },
]

// 반사강도 히트맵 구간 → 색상. 셰이더 생성과 범례 렌더링이 이 표 하나를 공유한다.
export const INTENSITY_HEAT_LEGEND: { threshold: number; label: string; color: string }[] = [
  { threshold: 50, label: '≤ 50 (저반사)', color: '#0000ff' },
  { threshold: 100, label: '51~100', color: '#00ff00' },
  { threshold: 200, label: '101~200', color: '#ffff00' },
  { threshold: Infinity, label: '> 200 (고반사)', color: '#ff0000' },
]

// 높이 색상 램프의 색 정지점(0~1) — 범례 그라디언트 바와 셰이더가 이 배열을 공유한다.
export const HEIGHT_LEGEND_STOPS: string[] = ['#0000ff', '#00ffff', '#00ff00', '#ffff00', '#ff0000']

function hexToVec3(hex: string): string {
  const n = parseInt(hex.replace('#', ''), 16)
  const r = ((n >> 16) & 255) / 255
  const g = ((n >> 8) & 255) / 255
  const b = (n & 255) / 255
  return `vec3(${r.toFixed(4)}, ${g.toFixed(4)}, ${b.toFixed(4)})`
}

// mago-3d-tiler(ExtensionStructuralMetadata.java)가 실제로 굽는 EXT_structural_metadata 프로퍼티 이름은
// AttributeType.INTENSITY/CLASSIFICATION.getName() 값 그대로인 대문자 "INTENSITY"/"CLASSIFICATION"이다.
//
// RGB는 프로퍼티가 아니라 glTF COLOR_0 정점 속성으로 직접 구워진다. Cesium3DTileStyle에 color 표현식을
// 하나라도 지정하면 이 정점 색 기반 콘텐츠에서는 완전히 대체(REPLACE)되어 원본 색이 사라지므로(레거시 배치
// 피처 기반 b3dm과 달리 HIGHLIGHT 블렌드가 적용되지 않음), style 객체에서 color 키 자체를 생략해야만
// COLOR_0이 그대로 렌더링된다.
//
// Classification/Intensity/높이는 3D Tiles 스타일링 언어(tileset.style)로는 애초에 렌더링이 불가능하다 —
// CesiumJS의 Model.applyStyle()이 glTF 기반(비-레거시 .pnts) 콘텐츠에서는 GPU 프로퍼티 어트리뷰트 경로를
// 타지 않고 feature 객체 없는 CPU 경로(applyColorAndShow)로 빠져 ${PROPERTY} 참조가 항상 undefined가 됨
// (CesiumJS packages/engine/Source/Scene/Model/Model.js 의 isPnts 분기 직접 확인).
// 반면 MetadataPipelineStage → CustomShaderPipelineStage 경로는 이 게이트가 없어 CustomShader로는
// fsInput.metadata.PROPERTY 형태로 정상 접근 가능(Documentation/CustomShaderGuide 확인) — 그래서 이
// 모드들은 tileset.style 대신 tileset.customShader 로 구현한다.
//
// 포인트 클라우드는 투명도 조정을 지원하지 않는다(항상 완전 불투명) — RGB 모드에서 style에 color 키를
// 넣으면 원본 COLOR_0이 사라지는 문제(위 설명)를 투명도 슬라이더로 우회할 방법이 없어 기능 자체를 제거함.

function buildClassificationShaderBody(): string {
  const branches = CLASSIFICATION_LEGEND
    .map(({ code, color }, i) => `${i === 0 ? 'if' : 'else if'} (cls == ${code}.0) { c = ${hexToVec3(color)}; }`)
    .join('\n    ')
  return `
    vec3 c = vec3(0.6);
    float cls = fsInput.metadata.CLASSIFICATION;
    ${branches}
    material.diffuse = c;
  `
}

function buildIntensityGrayShaderBody(): string {
  return `
    float i = clamp(fsInput.metadata.INTENSITY / 255.0, 0.0, 1.0);
    material.diffuse = vec3(i);
  `
}

function buildIntensityHeatShaderBody(): string {
  const finite = INTENSITY_HEAT_LEGEND.filter(({ threshold }) => threshold !== Infinity)
  const fallback = INTENSITY_HEAT_LEGEND.find(({ threshold }) => threshold === Infinity) ?? INTENSITY_HEAT_LEGEND[INTENSITY_HEAT_LEGEND.length - 1]
  const branches = finite
    .map(({ threshold, color }, i) => `${i === 0 ? 'if' : 'else if'} (i <= ${threshold}.0) { c = ${hexToVec3(color)}; }`)
    .join('\n    ')
  return `
    float i = fsInput.metadata.INTENSITY;
    vec3 c = ${hexToVec3(fallback.color)};
    ${branches}
    material.diffuse = c;
  `
}

// 높이 색상 램프(HEIGHT_LEGEND_STOPS 5단계)로 정규화된 높이(0~1)를 표시
function buildHeightShaderBody(): string {
  const stops = HEIGHT_LEGEND_STOPS.map(hexToVec3)
  const segments = stops.length - 1
  const branches = stops.slice(0, -1).map((from, i) => {
    const to = stops[i + 1]
    const lo = (i / segments).toFixed(4)
    const hi = ((i + 1) / segments).toFixed(4)
    const cond = i === segments - 1 ? 'else' : `${i === 0 ? 'if' : 'else if'} (t < ${hi})`
    return `${cond} { c = mix(${from}, ${to}, (t - ${lo}) / ${(1 / segments).toFixed(4)}); }`
  }).join('\n    ')
  return `
    float h = dot(fsInput.attributes.positionWC - u_surfacePoint, u_surfaceNormal);
    float t = clamp((h - u_minHeight) / max(u_maxHeight - u_minHeight, 0.001), 0.0, 1.0);
    vec3 c;
    ${branches}
    material.diffuse = c;
  `
}

export interface HeightReference {
  minHeight: number
  maxHeight: number
  surfacePoint: Cesium.Cartesian3
  surfaceNormal: Cesium.Cartesian3
}

// 타일셋 루트의 region 바운딩 볼륨에서 실제 고도 범위(m)와, 높이를 계산할 기준면(지표점+법선)을 얻는다.
// mago-3d-tiler 출력은 항상 region 바운딩 볼륨(west,south,east,north,minHeight,maxHeight)을 쓰므로
// tileset.root.boundingVolume.minimumHeight/maximumHeight 로 정확한 고도값을 바로 얻을 수 있다.
export function getHeightReference(tileset: Cesium.Cesium3DTileset): HeightReference | null {
  const root = tileset.root as unknown as { boundingVolume?: unknown } | undefined
  const region = root?.boundingVolume as {
    rectangle?: Cesium.Rectangle
    minimumHeight?: number
    maximumHeight?: number
  } | undefined
  if (!region?.rectangle || region.minimumHeight === undefined || region.maximumHeight === undefined) {
    return null
  }
  const center = Cesium.Rectangle.center(region.rectangle)
  const surfacePoint = Cesium.Cartesian3.fromRadians(center.longitude, center.latitude, 0.0)
  const surfaceNormal = Cesium.Ellipsoid.WGS84.geodeticSurfaceNormal(surfacePoint, new Cesium.Cartesian3())
  return { minHeight: region.minimumHeight, maxHeight: region.maximumHeight, surfacePoint, surfaceNormal }
}

// Classification/Intensity/높이 색상 + 점 크기를 모두 CustomShader 하나로 처리(항상 완전 불투명)
function buildPointCloudCustomShader(
  tileset: Cesium.Cesium3DTileset,
  mode: 'classification' | 'intensity-gray' | 'intensity-heat' | 'height',
  pointSize: number,
): Cesium.CustomShader {
  const heightRef = mode === 'height' ? getHeightReference(tileset) : null
  const body =
    mode === 'classification' ? buildClassificationShaderBody() :
    mode === 'intensity-gray' ? buildIntensityGrayShaderBody() :
    mode === 'intensity-heat' ? buildIntensityHeatShaderBody() :
    heightRef !== null ? buildHeightShaderBody() :
    // region 바운딩 볼륨이 없는 예외적인 경우 원본색 유지로 안전하게 대체
    'material.diffuse = fsInput.attributes.color_0.rgb;'

  return new Cesium.CustomShader({
    lightingModel: Cesium.LightingModel.UNLIT,
    uniforms: {
      u_pointSize: { type: Cesium.UniformType.FLOAT, value: pointSize },
      ...(heightRef !== null ? {
        u_minHeight: { type: Cesium.UniformType.FLOAT, value: heightRef.minHeight },
        u_maxHeight: { type: Cesium.UniformType.FLOAT, value: heightRef.maxHeight },
        u_surfacePoint: { type: Cesium.UniformType.VEC3, value: heightRef.surfacePoint },
        u_surfaceNormal: { type: Cesium.UniformType.VEC3, value: heightRef.surfaceNormal },
      } : {}),
    },
    vertexShaderText: `
      void vertexMain(VertexInput vsInput, inout czm_modelVertexOutput vsOutput) {
        vsOutput.pointSize = u_pointSize;
      }
    `,
    fragmentShaderText: `
      void fragmentMain(FragmentInput fsInput, inout czm_modelMaterial material) {
        ${body}
      }
    `,
  })
}

// 포인트 클라우드(las/laz → pnts) 타일셋에 색상 모드 + 점 크기를 적용(투명도 조정은 지원하지 않음 — 항상 불투명)
export function applyPointCloudStyle(tileset: Cesium.Cesium3DTileset, style: PointStyleState) {
  if (style.mode === 'rgb') {
    tileset.customShader = undefined
    // color 키를 아예 생략해야 원본 COLOR_0이 유지된다(위 설명 참고)
    tileset.style = new Cesium.Cesium3DTileStyle({ pointSize: style.pointSize })
  } else {
    // tileset.style을 남겨두면 CPU 경로(applyColorAndShow)가 같이 개입해 색을 덮어쓸 수 있어 비워둔다.
    tileset.style = undefined
    tileset.customShader = buildPointCloudCustomShader(tileset, style.mode, style.pointSize)
  }

  tileset.pointCloudShading.attenuation = style.attenuation
  tileset.pointCloudShading.maximumAttenuation = 4.0
}

// 메시(b3dm/i3dm) 등 일반 타일셋 — 흰색 틴트로 원본 색상은 유지한 채 투명도만 적용
// Gaussian Splat 타일셋은 feature/배치 테이블이 없어 Cesium3DTileStyle을 적용하면 스플랫이
// 통째로 사라진다(Cesium 공식 확인 사항) — 이 경우 스타일을 건드리지 않고 그대로 둔다.
export function applyOpacityOnlyStyle(tileset: Cesium.Cesium3DTileset, opacityPct: number) {
  if (Cesium.GaussianSplat3DTileContent.tilesetRequiresGaussianSplattingExt(tileset)) {
    tileset.style = undefined
    return
  }
  const alpha = Math.max(0, Math.min(100, opacityPct)) / 100
  tileset.style = new Cesium.Cesium3DTileStyle({ color: `color('white', ${alpha})` })
}
